import {
    Unsubscribe,
    get,
    onChildAdded,
    onChildRemoved,
    orderByChild,
    query,
    startAt,
} from '@/features/firebase/adapter/database'
import FirebaseDatabaseRefService from '@/features/firebase/services/database-ref'
import { logger } from '@/features/notifications/logger'
import { Notification } from '@/features/notifications/notification-service-extension-shared/types/app'
import { transformNotification } from '@/features/notifications/notification-service-extension-shared/utils/transforms'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import invariant from 'tiny-invariant'

export const useFirebaseNotificationsListeners = (): Notification[] => {
    const [notifications, setNotifications] = useState<Notification[]>([])

    useEffect(() => {
        let unsubscribeRemoved: Unsubscribe | null = null
        let unsubscribeAdd: Unsubscribe | null = null

        const listen = async () => {
            const initialTime = dayjs()

            const notificationsRef = FirebaseDatabaseRefService.notifications()

            unsubscribeRemoved = onChildRemoved(notificationsRef, (snapshot) => {
                invariant(snapshot.key, "Snapshot key doesn't exist")

                setNotifications((prevState) => prevState.filter(({ id }) => id !== snapshot.key))
            })

            const snapshot = await get(query(notificationsRef, orderByChild('createdAt')))

            if (snapshot.exists()) {
                const notifications: Notification[] = []

                snapshot.forEach((childSnapshot) => {
                    const notification = transformNotification(childSnapshot.val())

                    if (!notification) {
                        logger
                            .withMetadata({ extras: { notification } })
                            .warn('Unknown type of notification received')

                        return
                    }

                    notifications.unshift(notification)
                })

                setNotifications(notifications)
            }

            unsubscribeAdd = onChildAdded(
                query(notificationsRef, orderByChild('createdAt'), startAt(initialTime.unix())),
                (snapshot) => {
                    invariant(snapshot.key, "Snapshot key doesn't exist")

                    setNotifications((prevState) => {
                        const notification = transformNotification(snapshot.val())

                        if (!notification) {
                            logger
                                .withMetadata({ extras: { notification } })
                                .warn('Unknown type of notification received')

                            return prevState
                        }

                        if (prevState.some(({ id }) => id === notification.id)) return prevState

                        return [notification, ...prevState]
                    })
                }
            )
        }

        listen()

        return () => {
            if (unsubscribeRemoved) unsubscribeRemoved()
            if (unsubscribeAdd) unsubscribeAdd()
        }
    }, [])

    return notifications
}
